import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import Button from '@/components/Button';
import Dialog, {
	DialogContent,
	DialogFooter,
	DialogTop
} from '@/components/Dialog';
import Input from '@/components/form/Input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/form/Select';
import Switch from '@/components/form/Switch';
import { useShallowEqualSelector } from '@/hooks';
import {
	setCreateCollaboratorModal,
	setSelectedCollaborator
} from '@/store/collaborators';
import {
	createCollaborator,
	editCollaborator
} from '@/store/collaborators/thunks';
import { cn, showError, showSuccess } from '@/utils';

import { MultiSelect } from '../../../components/Multiselect';
import Spinner from '../../../components/Spinner';
import {
	DEFAULT_COLLABORATOR_VIEWS,
	DISABLED_VIEWS_FOR_LIMITED,
	ERROR_CODES,
	ROLES,
	VIEWS_NAMES
} from './constants';
import { useRoles, useViews } from './hooks';

function CollaboratorsModal() {
	const dispatch = useDispatch();
	const [selectedFolders, setSelectedFolders] = useState([]);
	const [selectedViews, setSelectedViews] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const { t } = useTranslation();

	const roles = useRoles();
	const views = useViews();

	const { isOpen, folders, selectedCollaborator } = useShallowEqualSelector(
		({ myDocuments, collaborators }) => ({
			isOpen: collaborators.collaboratorsModalOpen,
			folders: myDocuments.folders || [],
			selectedCollaborator: collaborators.selectedCollaborator
		})
	);

	const isEdit = !!selectedCollaborator;

	const schema = Yup.object({
		email: Yup.string().email().required(),
		role: Yup.number().required()
	});

	const methods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			email: '',
			role: ROLES.admin.toString(),
			canDeleteAccount: selectedCollaborator?.canDeleteAccount || true
		},
		mode: 'onChange'
	});

	const roleSelected = methods.watch('role');
	const email = methods.watch('email');
	const role = methods.watch('role');

	const isRoleExtendedCollaborator =
		Number(roleSelected) === ROLES.extendedCollaborator;
	const isRoleLimited = Number(roleSelected) === ROLES.collaborator;
	const isAdminRole = Number(roleSelected) === ROLES.admin;

	const isDisabled = !email || !role || isLoading;

	let viewDisabledOptions = [];

	if (isRoleExtendedCollaborator) {
		viewDisabledOptions = DEFAULT_COLLABORATOR_VIEWS;
	}
	if (isRoleLimited) {
		viewDisabledOptions = DISABLED_VIEWS_FOR_LIMITED;
	}

	const handleUpdateExtendedCollaboratorViews = () => {
		setSelectedViews(vs => [
			...DEFAULT_COLLABORATOR_VIEWS,
			...vs.filter(v => !DEFAULT_COLLABORATOR_VIEWS.includes(v))
		]);
	};

	const handleUpdateLimitedViews = () => {
		setSelectedViews(vs => vs.filter(v => v !== VIEWS_NAMES.EDITOR));
	};

	useEffect(() => {
		if (isRoleExtendedCollaborator) {
			handleUpdateExtendedCollaboratorViews();
		}
		if (isRoleLimited) {
			handleUpdateLimitedViews();
		}
	}, [role]);

	useEffect(() => {
		if (isEdit) {
			methods.setValue('email', selectedCollaborator?.email);
			methods.setValue('role', selectedCollaborator?.role?.toString());
			methods.setValue(
				'canDeleteAccount',
				selectedCollaborator?.canDeleteAccount
			);
			setSelectedFolders(selectedCollaborator?.folderIds || []);
			const enabledViews = Object.entries(selectedCollaborator?.views || {})
				.filter(([, enabled]) => enabled)
				.map(([viewName]) => viewName);
			setSelectedViews(enabledViews);
		}
	}, [isEdit]);

	const onClose = () => {
		dispatch(setCreateCollaboratorModal(false));
		dispatch(setSelectedCollaborator(null));
		methods.reset();
	};

	const handleCreateCollaborator = async values => {
		setIsLoading(true);
		const viewsObject = views.reduce(
			(acc, view) => ({
				...acc,
				[view.value]: selectedViews.includes(view.value)
			}),
			{}
		);

		dispatch(
			createCollaborator({
				...values,
				folderIds: selectedFolders,
				views: viewsObject
			})
		)
			.unwrap()
			.then(() => {
				setSelectedFolders([]);
				setSelectedViews([]);
				showSuccess(t('users.userModal.created'));
				onClose();
			})
			.catch(error => {
				if (error?.data?.errorCode === ERROR_CODES.email_exists) {
					showError(t('errors.emailExists'));
				} else if (error?.data?.errorCode === ERROR_CODES.email_invalid) {
					showError(t('errors.emailInvalid'));
				} else if (
					error?.data?.errorCode === ERROR_CODES.user_account_closed
				) {
					showError(t('errors.userAccountClosed'));
				} else {
					showError(error);
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const handleEditCollaborator = async values => {
		setIsLoading(true);
		const viewsObject = views.reduce(
			(acc, view) => ({
				...acc,
				[view.value]: selectedViews.includes(view.value)
			}),
			{}
		);

		const { email: _email, canDeleteAccount, ...valuesWithoutEmail } = values;

		dispatch(
			editCollaborator({
				...valuesWithoutEmail,
				canDeleteAccount: !!canDeleteAccount || false,
				folderIds: selectedFolders,
				views: viewsObject,
				collaboratorId: selectedCollaborator?.id
			})
		)
			.unwrap()
			.then(() => {
				setSelectedFolders([]);
				setSelectedViews([]);
				showSuccess(t('users.userModal.userEdited'));
				onClose();
			})
			.catch(error => {
				showError(error);
			})
			.finally(() => {
				setIsLoading(false);
				setSelectedCollaborator(null);
			});
	};

	const foldersOptions = folders.map(folder => ({
		value: folder.id,
		label: folder.name
	}));

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="w-[280px] md:w-[397px]">
				<DialogTop
					title={
						isEdit ? t('users.userModal.editUser') : t('users.addUser')
					}
				/>

				<FormProvider {...methods}>
					<form
						onSubmit={
							isEdit
								? methods.handleSubmit(handleEditCollaborator)
								: methods.handleSubmit(handleCreateCollaborator)
						}
					>
						<div className={cn('flex flex-col px-4 py-3')}>
							<Input
								name="email"
								label={t('users.userModal.email')}
								placeholder={t('account.form.placeholder.email')}
								size="lg"
								className="focus-visible:outline-none"
								disabled={isEdit}
							/>
						</div>
						<div className="flex flex-col px-4 py-3">
							<Select name="role" label={t('users.userModal.role')}>
								<SelectTrigger className="group border-gray-200 bg-white p-0 text-grey-500 group-hover:text-grey-500">
									<SelectValue />
								</SelectTrigger>
								<SelectContent className="relative bg-white text-grey-500">
									{isLoading && (
										<Spinner
											className="absolute inset-0 m-auto text-grey-500"
											size={24}
										/>
									)}
									{Object.entries(roles).map(([value, label]) => (
										<SelectItem
											className="text-grey-500 hover:bg-grey-100 data-[state=checked]:bg-grey-100 data-[state=checked]:text-black"
											key={value}
											value={value}
										>
											{label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						{!isAdminRole && (
							<>
								<div className="flex flex-col px-4 py-3">
									<MultiSelect
										options={foldersOptions}
										selected={selectedFolders}
										onChange={setSelectedFolders}
										placeholder={t('users.userModal.select')}
										labelText={t('users.userModal.folders')}
										searchable
									/>
								</div>

								<div className="flex flex-col px-4 py-3">
									<MultiSelect
										disabledOptions={viewDisabledOptions}
										options={views || []}
										selected={selectedViews}
										onChange={setSelectedViews}
										placeholder={t('users.userModal.select')}
										labelText={t('users.userModal.views')}
									/>
								</div>
							</>
						)}

						<div className="flex flex-col px-5 pb-5">
							<Switch
								id="canDeleteAccount"
								label={t('users.userModal.canDeleteAccount')}
								name="canDeleteAccount"
								className="mt-4"
							/>
						</div>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={event => {
									event.preventDefault();
									onClose(false);
								}}
							>
								{t('common.cancel')}
							</Button>
							<Button
								type="submit"
								loading={isLoading}
								disabled={isDisabled}
							>
								{isEdit ? t('common.update') : t('common.create')}
							</Button>
						</DialogFooter>
					</form>
				</FormProvider>
			</DialogContent>
		</Dialog>
	);
}

export default CollaboratorsModal;
